<template>
  <div class="mt-5">
    <b-container fluid>
      <h1 class="app-page-title mb-4">Download Student Data</h1>

      <div class="card mb-4">
        <div class="card-header bg-primary text-white">
          <b-icon icon="funnel" class="mr-2"></b-icon>Filter Students
        </div>
        <div class="card-body">
          <div class="row">
            <!-- Student Status -->
            <div class="col-md-4 mb-3">
              <label class="small font-weight-bold">Student Status</label>
              <b-form-select v-model="filters.status" size="sm">
                <option value="">All Students</option>
                <option value="CURRENT">Current</option>
                <option value="GRADUATED">Graduated</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="WITHDRAWN">Withdrawn</option>
              </b-form-select>
            </div>

            <!-- Faculty/College -->
            <div class="col-md-4 mb-3">
              <label class="small font-weight-bold">Faculty / College</label>
              <v-select
                v-model="filters.college_id"
                :options="collegeOptions"
                :reduce="c => c.value"
                placeholder="All Faculties"
                :clearable="true"
                class="vs-sm"
                @input="onCollegeChange"
              ></v-select>
            </div>

            <!-- Department -->
            <div class="col-md-4 mb-3">
              <label class="small font-weight-bold">Department</label>
              <v-select
                v-model="filters.department_id"
                :options="filteredDepartmentOptions"
                :reduce="d => d.value"
                placeholder="All Departments"
                :clearable="true"
                class="vs-sm"
                @input="onDepartmentChange"
              ></v-select>
            </div>

            <!-- Programme -->
            <div class="col-md-4 mb-3">
              <label class="small font-weight-bold">Programme</label>
              <v-select
                v-model="filters.programme_id"
                :options="filteredProgrammeOptions"
                :reduce="p => p.value"
                placeholder="All Programmes"
                :clearable="true"
                class="vs-sm"
              ></v-select>
            </div>

            <!-- Grant Type -->
            <div class="col-md-4 mb-3">
              <label class="small font-weight-bold">Grant Type</label>
              <b-form-select v-model="filters.grant_type" size="sm">
                <option value="">All</option>
                <option value="RUN">RUN</option>
                <option value="TETFUND">TETFUND</option>
                <option value="OTHERS">OTHERS</option>
              </b-form-select>
            </div>

            <!-- Level / Year of Study -->
            <div class="col-md-4 mb-3">
              <label class="small font-weight-bold">Level / Year of Study</label>
              <b-form-select v-model="filters.year" size="sm">
                <option value="">All Years</option>
                <option v-for="y in [1,2,3,4,5,6]" :key="y" :value="y">Year {{ y }}</option>
              </b-form-select>
            </div>

            <!-- Admission Session -->
            <div class="col-md-4 mb-3">
              <label class="small font-weight-bold">Admission Session</label>
              <v-select
                v-model="filters.admission_session"
                :options="sessionOptions"
                placeholder="All Sessions"
                :clearable="true"
                class="vs-sm"
              ></v-select>
            </div>

            <!-- Paid in Session -->
            <div class="col-md-4 mb-3">
              <label class="small font-weight-bold">Paid Fees in Session</label>
              <v-select
                v-model="filters.paid_session"
                :options="sessionOptions"
                placeholder="Any Session"
                :clearable="true"
                class="vs-sm"
              ></v-select>
            </div>
          </div>

          <!-- Active filter tags -->
          <div class="mt-2" v-if="activeFilterCount > 0">
            <span class="small text-muted mr-2">Active filters ({{ activeFilterCount }}):</span>
            <b-badge
              v-for="(val, key) in activeFilters"
              :key="key"
              variant="info"
              pill
              class="mr-1 mb-1"
              style="cursor: pointer"
              @click="clearFilter(key)"
            >
              {{ filterLabel(key) }}: {{ filterDisplayValue(key, val) }}
              <b-icon icon="x" class="ml-1"></b-icon>
            </b-badge>
            <b-badge variant="secondary" pill class="mr-1 mb-1" style="cursor: pointer" @click="clearAllFilters">
              Clear All <b-icon icon="x" class="ml-1"></b-icon>
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Download Actions -->
      <div class="card">
        <div class="card-body text-center py-4">
          <p class="text-muted mb-3">
            Click the button below to download student data matching your selected filters as an Excel file.
          </p>
          <button
            class="btn btn-success btn-lg"
            @click="downloadData"
            :disabled="downloading"
          >
            <b-spinner small v-if="downloading" class="mr-2"></b-spinner>
            <b-icon v-else icon="download" class="mr-2"></b-icon>
            {{ downloading ? 'Preparing Download...' : 'Download Excel' }}
          </button>
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "StudentDataDownload",
  data() {
    return {
      loading: false,
      downloading: false,
      filters: {
        status: "",
        college_id: "",
        department_id: "",
        programme_id: "",
        grant_type: "",
        year: "",
        admission_session: "",
        paid_session: "",
      },
      sessions: [],
      colleges: [],
      departments: [],
      programmes: [],
    };
  },
  computed: {
    sessionOptions() {
      return this.sessions.map((s) => s);
    },
    collegeOptions() {
      return this.colleges.map((c) => ({
        label: c.college,
        value: c.id,
      }));
    },
    filteredDepartmentOptions() {
      let depts = this.departments;
      if (this.filters.college_id) {
        depts = depts.filter((d) => d.college_id === this.filters.college_id);
      }
      return depts.map((d) => ({
        label: d.department + (d.college ? " (" + d.college.college + ")" : ""),
        value: d.id,
      }));
    },
    filteredProgrammeOptions() {
      let progs = this.programmes;
      if (this.filters.department_id) {
        progs = progs.filter(
          (p) => p.department_id === this.filters.department_id
        );
      } else if (this.filters.college_id) {
        const deptIds = this.departments
          .filter((d) => d.college_id === this.filters.college_id)
          .map((d) => d.id);
        progs = progs.filter((p) => deptIds.includes(p.department_id));
      }
      return progs.map((p) => ({
        label: (p.code ? p.code + " - " : "") + p.programme + " (" + (p.degree || p.prog_type || "") + ")",
        value: p.id,
      }));
    },
    activeFilters() {
      const active = {};
      for (const [key, val] of Object.entries(this.filters)) {
        if (val !== "" && val !== null && val !== undefined) {
          active[key] = val;
        }
      }
      return active;
    },
    activeFilterCount() {
      return Object.keys(this.activeFilters).length;
    },
  },
  methods: {
    async loadFilterOptions() {
      this.loading = true;
      try {
        const { data } = await axios.get("admin/student-data/filter-options");
        this.sessions = data.sessions || [];
        this.colleges = data.colleges || [];
        this.departments = data.departments || [];
        this.programmes = data.programmes || [];
      } catch (err) {
        console.error("Failed to load filter options", err);
      }
      this.loading = false;
    },
    onCollegeChange() {
      this.filters.department_id = "";
      this.filters.programme_id = "";
    },
    onDepartmentChange() {
      this.filters.programme_id = "";
    },
    filterLabel(key) {
      const labels = {
        status: "Status",
        college_id: "Faculty",
        department_id: "Dept",
        programme_id: "Programme",
        grant_type: "Grant",
        year: "Year",
        admission_session: "Admitted",
        paid_session: "Paid Session",
      };
      return labels[key] || key;
    },
    filterDisplayValue(key, val) {
      if (key === "college_id") {
        const c = this.colleges.find((x) => x.id === val);
        return c ? c.college : val;
      }
      if (key === "department_id") {
        const d = this.departments.find((x) => x.id === val);
        return d ? d.department : val;
      }
      if (key === "programme_id") {
        const p = this.programmes.find((x) => x.id === val);
        return p ? (p.code || p.programme) : val;
      }
      if (key === "year") return "Year " + val;
      return val;
    },
    clearFilter(key) {
      this.filters[key] = "";
    },
    clearAllFilters() {
      for (const key of Object.keys(this.filters)) {
        this.filters[key] = "";
      }
    },
    async downloadData() {
      this.downloading = true;
      try {
        const params = {};
        for (const [key, val] of Object.entries(this.filters)) {
          if (val !== "" && val !== null && val !== undefined) {
            params[key] = val;
          }
        }

        const response = await axios.get("admin/student-data/download", {
          params,
          responseType: "blob",
        });

        const contentDisposition = response.headers["content-disposition"];
        let filename = "students.xlsx";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match && match[1]) {
            filename = match[1].replace(/['"]/g, "");
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        Swal.fire({
          icon: "success",
          title: "Download Started",
          text: "Your student data file is downloading.",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error("Download failed", err);
        if (err.response && err.response.data instanceof Blob) {
          const text = await err.response.data.text();
          try {
            const json = JSON.parse(text);
            Swal.fire("Error", json.message || "Download failed", "error");
          } catch (e) {
            Swal.fire("Error", "Download failed. Please try again.", "error");
          }
        } else {
          Swal.fire("Error", "Download failed. Please try again.", "error");
        }
      }
      this.downloading = false;
    },
  },
  created() {
    this.loadFilterOptions();
  },
};
</script>

<style scoped>
.small-box {
  border-radius: 0.5rem;
  padding: 1rem;
  color: white;
}
.small-box .inner h4 {
  font-size: 1.5rem;
  font-weight: bold;
}
.vs-sm .vs__dropdown-toggle {
  min-height: 31px;
  font-size: 0.875rem;
}
</style>
